import React, { useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { API } from '../../controllers/bulkActionStatus';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../utils/logger';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ApplicationMessageForBulkAction from './ApplicationMessageForBulkAction';
import BulkTransactionErrorModal from './BulkTransactionErrorModal';
import { OperationTypes } from '../../utils/enums';

const log = new Logger('BULK_TRANSACTION_STATUS_DATA', LOG_LEVELS.DEBUG);
const pageLog = new Logger('BULK_ACTION_PAGE', LOG_LEVELS.DEBUG);
const PAGE_NAME = "BULK_ACTION_STATUS_DATA_PAGE";

const BulkActionStatusData = ({ dispatch, operations, loading, showModal, setShowModal, selectedOperation, setSelectedOperation, tenantId, userId }) => {
    const [refreshCounter, setRefreshCounter] = useState(0);
    const operationsArray = Array.isArray(operations) ? operations : [];

    useEffect(() => {
        return () => {
            dispatch({ type: 'CLEAR_OPERATIONS' });
            setRefreshCounter(0);
        };
    }, []);

    const onMessageClickHandler = useCallback(
        (failedOperation) => {
            setSelectedOperation(failedOperation);
            setShowModal(true);
        },
        [setShowModal, setSelectedOperation]
    );

    const closeModal = () => {
        setShowModal(false);
        setSelectedOperation(null);
    };

    const getOperationName = (operationType) => {
        if (operationType?.includes('BulkDelete')) {
            return 'Delete';
        }
        else if (operationType?.includes('BulkRedeliver')) {
            return 'Redeliver';
        }
        else if (operationType?.includes('BulkForceToError')) {
            return 'Force to Error';
        }
        else {
            return 'Unknown';
        }
    }

    const createMessages = (operation) => {
        if (!operation?.status) {
            log.debug("Operation or operation.status is missing");
            return [];
        }
        let messageConfig;

        switch (operation.status) {
            case 'Failed':
                messageConfig = {
                    languageString: "inAppMessage_App_Error_167",
                    extendedLanguageString: "inAppMessage_App_Error_167_extended",
                    jobName: operation.jobName,
                    operationName: getOperationName(operation.operationType)
                };
                break;

            case 'Success':
                messageConfig = {
                    languageString: "inAppMessage_Success_34",
                    extendedLanguageString: "inAppMessage_Success_34_extended",
                    jobName: operation.jobName,
                    operationName: getOperationName(operation.operationType)
                };
                break;

            case 'InProgress':
                messageConfig = {
                    languageString: "inAppMessage_Info_7",
                    extendedLanguageString: "inAppMessage_Info_7_extended",
                    jobName: operation.jobName,
                    operationName: getOperationName(operation.operationType)
                };
                break;

            default:
                log.debug(`Unhandled operation state: ${operation.status}`);
                return [];
        }

        return [{
            message: messageConfig,
            args: [messageConfig.operationName]
        }];
    };

    const getOperationTypeIds = () => [
        OperationTypes.BulkDeleteDocuments,
        OperationTypes.BulkDeleteInvoices,
        OperationTypes.BulkRedeliverDocuments,
        OperationTypes.BulkForceToErrorDocuments,
        OperationTypes.BulkRedeliverInvoices,
        OperationTypes.BulkForceToErrorInvoices
    ];

    const handleOperationStatus = useCallback(() => {
        pageLog.clearDictionaryError(ALL_MESSAGES.BULK_ACTION_INITIATED);
        setRefreshCounter(prev => prev + 1);
        // only invoice id and document id specific bulk operations
        const operationTypeIds = getOperationTypeIds();
        dispatch(API.getOperations(tenantId, userId, operationTypeIds,
            (response) => {
                log.debug("Success fetching Operations Status");
            },
            (error) => {
                log.debug("Error fetching operations : " + error)
            }
        ));
    }, [dispatch, setRefreshCounter]);

    return (
        <div>
            <button
                name="Status"
                className={'secondary mb20'}
                disabled={loading}
                data-testid='status-button'
                onClick={handleOperationStatus}
            >
                <LanguageStringResolver
                    string={'Button_BulkAction_Status'}
                    tooltip={'Button_BulkAction_Status_Extended'}
                />
            </button>
            {/* Show no transactions message if operations is empty */}

            {refreshCounter > 0 && (!operationsArray || operationsArray.length === 0) && (
                <ApplicationMessageForBulkAction
                    key={`no-transactions-${refreshCounter}`}
                    messages={[{
                        message: {
                            languageString: 'inAppMessage_Info_8',
                            extendedLanguageString: "inAppMessage_Info_8_extended"
                        }
                    }]}
                    messageType="applicationInfo"
                    scopes={[PAGE_NAME]}
                    disableDismiss={false}
                />
            )}
            {operationsArray?.map((operation) => {
                if (operation.status === "Success") {
                    return (
                        <ApplicationMessageForBulkAction
                            key={`${operation.operationId}-${refreshCounter}`}
                            messages={createMessages(operation)}
                            messageIds={[ALL_MESSAGES.BULK_ACTION_COMPLETED]}
                            messageType="applicationSuccess"
                            scopes={[PAGE_NAME]}
                            disableDismiss={false}
                        />
                    );
                } else if (operation.status === "InProgress") {
                    return (
                        <ApplicationMessageForBulkAction
                            key={`${operation.operationId}-${refreshCounter}`}
                            messages={createMessages(operation)}
                            messageIds={[ALL_MESSAGES.BULK_ACTION_IN_PROGRESS]}
                            messageType="applicationInfo"
                            scopes={[PAGE_NAME]}
                            disableDismiss={false}
                        />
                    );
                } else if (operation.status === "Failed") {
                    return (
                        <ApplicationMessageForBulkAction
                            key={`${operation.operationId}-${refreshCounter}`}
                            messageIds={[ALL_MESSAGES.BULK_ACTION_FAILED]}
                            messages={createMessages(operation)}
                            messageType="applicationError"
                            scopes={[PAGE_NAME]}
                            disableDismiss={false}
                        >
                            <span>
                                &nbsp;
                                <a onClick={() => onMessageClickHandler(operation)}>
                                    <LanguageStringResolver
                                        string={operation.status === "Failed"
                                            ? "BulkAction_CompleteError"
                                            : "BulkAction_partailError"
                                        }
                                    />
                                </a>
                            </span>
                        </ApplicationMessageForBulkAction>
                    );
                }
                return null;
            })
            }
            {selectedOperation && <BulkTransactionErrorModal operation={selectedOperation} showModal={showModal} onClose={closeModal} />}
        </div>

    );
};


BulkActionStatusData.defaultProps = {
    operations: [],
    loading: false,
    selectedOperation: null
};

const mapStateToProps = (state) => ({
    operations: Array.isArray(state.bulkActionStatus?.operations)
        ? state.bulkActionStatus.operations
        : [],
    loading: state.bulkActionStatus?.loading || false,
    error: state.bulkActionStatus?.error || null
});

export default connect(mapStateToProps)(BulkActionStatusData);